import { useState } from 'react';
import { roadmap } from '../mock';
import { MapPin, ArrowUpRight } from 'lucide-react';
import { TopoLines } from './graphics/Decor';
import WAMap from './WAMap';

const toneStyles = {
  live: { ring: 'ring-emerald-200/70', dot: 'bg-emerald-500', label: 'text-emerald-700 bg-emerald-50 border-emerald-200/70' },
  soon: { ring: 'ring-sky-200/70', dot: 'bg-sky-500', label: 'text-sky-800 bg-sky-50 border-sky-200/70' },
  later: { ring: 'ring-indigo-200/70', dot: 'bg-indigo-500', label: 'text-indigo-800 bg-indigo-50 border-indigo-200/70' },
  roadmap: { ring: 'ring-slate-200/70', dot: 'bg-slate-400', label: 'text-slate-700 bg-slate-50 border-slate-200/80' },
};

const groupByTone = Object.fromEntries(roadmap.map((group) => [group.tone, group]));

const RoadmapHeader = () => (
  <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
    <div className="max-w-2xl">
      <div className="text-[12px] uppercase tracking-[0.22em] font-semibold text-[#3E5C76]">
        ART — AI Rendered Title
      </div>
      <h2 className="mt-3 font-serif text-4xl md:text-5xl font-medium tracking-[-0.02em] text-[#0B2545]">
        County expansion roadmap
      </h2>
      <p className="mt-5 text-[16px] leading-relaxed text-slate-600">
        ART is rolling out county by county across all 39 Washington State counties. Hover or tap any county to explore.
      </p>
    </div>
    <a
      href="https://art.titlecat.com/"
      target="_blank"
      rel="noreferrer"
      className="group inline-flex items-center gap-2 self-start rounded-full border border-slate-300 bg-white hover:bg-slate-50 text-[#0B2545] text-[13.5px] font-medium px-5 py-2.5 transition-colors"
    >
      Open ART
      <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
    </a>
  </div>
);

const SegmentedControl = ({ active, onChange }) => (
  <div className="mt-8 inline-flex flex-wrap gap-1.5 rounded-full border border-slate-200 bg-slate-50 p-1">
    {roadmap.map((r) => (
      <button
        key={r.tone}
        onClick={() => onChange(r.tone)}
        data-testid={`roadmap-tab-${r.tone}`}
        className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-[12.5px] font-medium transition-all ${
          active === r.tone ? 'bg-white shadow-sm text-[#0B2545]' : 'text-slate-500 hover:text-[#0B2545]'
        }`}
      >
        <span className={`w-1.5 h-1.5 rounded-full ${toneStyles[r.tone].dot}`} />
        {r.label}
        <span className="text-[11px] text-slate-400 font-normal">({r.counties.length})</span>
      </button>
    ))}
  </div>
);

const CountyPanel = ({ group, selectedCounty, onSelect }) => (
  <div className={`rounded-2xl border border-slate-200 bg-white p-6 shadow-sm ring-4 ${toneStyles[group.tone].ring}`} data-testid="county-panel">
    <div className={`inline-flex items-center gap-2 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${toneStyles[group.tone].label}`} data-testid="county-panel-label">
      <span className={`w-1.5 h-1.5 rounded-full ${toneStyles[group.tone].dot}`} />
      {group.label} — {group.counties.length}
    </div>
    <ul className="mt-4 grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-1 gap-x-4 gap-y-1.5 max-h-[420px] overflow-auto pr-1">
      {group.counties.map((c) => (
        <li
          key={c}
          onClick={() => onSelect(c)}
          data-testid={`county-item-${c.replace(/\s+/g, '-').toLowerCase()}`}
          className={`flex items-center gap-2 text-[13.5px] cursor-pointer rounded-md px-1.5 py-1 transition-colors ${
            selectedCounty === c ? 'bg-slate-100 text-[#0B2545] font-semibold' : 'text-slate-700 hover:bg-slate-50'
          }`}
        >
          <MapPin className="w-3.5 h-3.5 text-slate-400" />
          {c}
        </li>
      ))}
    </ul>
  </div>
);

const Roadmap = () => {
  const [active, setActive] = useState('live');
  const [selectedCounty, setSelectedCounty] = useState(null);
  const activeGroup = groupByTone[active];

  const onSelectCounty = (name) => {
    setSelectedCounty(name);
    const group = roadmap.find((g) => g.counties.includes(name));
    if (group) setActive(group.tone);
  };

  return (
    <section id="roadmap" className="relative py-24 md:py-32 bg-white overflow-hidden">
      <TopoLines className="absolute bottom-0 left-0 w-full h-40 pointer-events-none" opacity={0.35} />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <RoadmapHeader />
        <SegmentedControl active={active} onChange={(tone) => { setActive(tone); setSelectedCounty(null); }} />
        <div className="mt-8 grid lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-8 rounded-3xl border border-slate-200 bg-gradient-to-br from-white to-[#F1F4F8] p-4 md:p-8 shadow-sm">
            <WAMap activeTone={active} selectedCounty={selectedCounty} onSelectCounty={onSelectCounty} />
          </div>
          <div className="lg:col-span-4">
            {activeGroup && (
              <CountyPanel group={activeGroup} selectedCounty={selectedCounty} onSelect={onSelectCounty} />
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Roadmap;
